import type { ReactNode } from "react";

import type { FichaDoClubeView, Sector } from "../game/types";
import { sectorOf } from "../game/views";
import { rotuloDePosicao, useT } from "../i18n";
import { Crest, FormChips, strengthColor } from "../ui/atoms";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { MiniCampo } from "./MiniCampo";
import "./clube.css";

/**
 * Outro clube visto de fora.
 *
 * Abre ao tocar no clube onde ele aparece: no placar da partida, nos placares
 * da rodada, no próximo jogo, no calendário, na tabela e no adversário da
 * Tática. Antes o nome era texto solto, e no celular o toque oferecia
 * pesquisar no Google.
 *
 * Mostra o time provável, que é a escalação que o motor usaria se a rodada
 * rodasse agora, e o elenco inteiro por setor. Tocar num jogador abre a ficha
 * dele, quando quem abriu sabe abrir (fora da partida).
 */

const SETORES: Sector[] = ["GOL", "DEF", "MEI", "ATA"];

/**
 * O clube tocável: um botão que abre a ficha, com o que vier dentro (escudo,
 * nome). Sem quem abra, fica só o conteúdo, e o texto não é selecionável para o
 * toque não virar pesquisa.
 */
export function BotaoDoClube({
  clubId,
  nome,
  aoAbrir,
  className,
  children,
}: {
  clubId: string;
  nome: string;
  aoAbrir?: ((clubId: string) => void) | undefined;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const t = useT();
  if (!aoAbrir) return <span className={`clube-toque${className ? ` ${className}` : ""}`}>{children}</span>;
  return (
    <button
      type="button"
      className={`clube-botao${className ? ` ${className}` : ""}`}
      aria-label={t("clubeDeFora.abrir", { nome })}
      onClick={(evento) => {
        evento.stopPropagation();
        aoAbrir(clubId);
      }}
    >
      {children}
    </button>
  );
}

export function FichaDoClube({
  clube,
  aoFechar,
  aoAbrirJogador,
}: {
  clube: FichaDoClubeView;
  aoFechar: () => void;
  aoAbrirJogador?: ((playerId: string) => void) | undefined;
}): ReactNode {
  const t = useT();
  // Acima da partida (28), que também escuta o Esc: a ficha abre por cima dela.
  useDialogoDeAtalhos({ nome: "Ficha do clube", nivel: 30, aoFechar });

  return (
    <div className="clube-fundo" role="dialog" aria-modal="true" aria-label={t("clubeDeFora.aria", { nome: clube.name })} onClick={aoFechar}>
      <section className="clube-ficha card" onClick={(evento) => evento.stopPropagation()}>
        <header className="clube-topo">
          <Crest colors={clube.colors} short={clube.short} size={44} />
          <div className="clube-titulo">
            <b>{clube.name}</b>
            <span className="label-muted">
              {clube.competition}
              {clube.hasTable && ` · ${t("tatica.posicaoEPontos", { posicao: clube.position, pontos: clube.points })}`}
            </span>
            {clube.manager && <span className="muted clube-tecnico">{clube.manager}</span>}
          </div>
          <button type="button" className="ghost clube-fechar" aria-label={t("comum.fechar")} onClick={aoFechar}>
            ×
          </button>
        </header>

        <div className="clube-corpo">
          <div className="clube-provavel">
            <div className="clube-provavel-topo">
              <span className="label-muted">{t("clubeDeFora.timeProvavel")}</span>
              <FormChips form={clube.form} />
            </div>
            <MiniCampo
              className="clube-campo"
              formacao={clube.formation}
              vagas={clube.slots}
              titulares={clube.starters}
              cores={clube.colors}
            />
          </div>

          <div className="clube-elenco">
            <div className="clube-elenco-topo">
              <span className="label-muted">
                {t("clubeDeFora.elenco")} <span className="num">{clube.squad.length}</span>
              </span>
              <span className="label-muted clube-ga">{t("tatica.golsAssist")}</span>
              <span className="label-muted clube-idade">{t("clubeDeFora.idade")}</span>
              <span className="label-muted clube-forca-rotulo">{t("elenco.colunas.forca")}</span>
            </div>
            {SETORES.map((setor) => {
              const jogadores = clube.squad.filter((jogador) => sectorOf(jogador.pos) === setor);
              if (jogadores.length === 0) return null;
              return (
                <div key={setor} className="clube-setor">
                  <h4 className="label-muted">{t(`comum.setores.${setor}`)}</h4>
                  <ul>
                    {jogadores.map((jogador) => {
                      const conteudo = (
                        <>
                          <span className="tatica-pos-linha" data-pos={jogador.pos}>
                            {rotuloDePosicao(jogador.pos)}
                          </span>
                          <span className="num clube-camisa">{jogador.shirt ?? ""}</span>
                          <span className="clube-nome">
                            {jogador.name}
                            {jogador.starter && <i className="clube-titular" aria-label={t("clubeDeFora.titular")} />}
                          </span>
                          <span className="clube-alertas">
                            {jogador.injuryRounds > 0 && (
                              <span className="tatica-al lesao" title={t("tatica.tituloLesionado", { rodadas: jogador.injuryRounds })}>
                                {jogador.injuryRounds}
                              </span>
                            )}
                            {jogador.suspensionRounds > 0 && (
                              <span className="tatica-al suspenso" title={t("tatica.tituloSuspenso", { rodadas: jogador.suspensionRounds })}>
                                {jogador.suspensionRounds}
                              </span>
                            )}
                          </span>
                          <span className="num clube-ga">
                            {jogador.goals}/{jogador.assists}
                          </span>
                          <span className="muted num clube-idade">{jogador.age}</span>
                          <span className="num clube-forca" style={{ background: strengthColor(jogador.strength) }}>
                            {jogador.strength}
                          </span>
                        </>
                      );
                      return (
                        <li key={jogador.id}>
                          {aoAbrirJogador ? (
                            <button type="button" className="clube-jogador" onClick={() => aoAbrirJogador(jogador.id)}>
                              {conteudo}
                            </button>
                          ) : (
                            <div className="clube-jogador">{conteudo}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
